import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ActionItemSummary } from '../models/pipeline';

/**
 * Phase 1 (SPEC v2 §6.8) — bottom-bar breakdown surface.
 *
 * Two-call flow: `propose()` returns an AI-generated preview the user
 * confirms/edits in the floating card; `materialize()` then creates the
 * parent + subtasks atomically. The preview step does not write anything
 * to the database, so canceling out of the card has zero side effects.
 */

export type BreakdownDepth = 'Quick' | 'Standard' | 'Detailed';

export interface BreakdownSubtask {
  title: string;
  estimateMinutes: number;
}

export interface BreakdownProposal {
  parentTitle: string;
  subtasks: BreakdownSubtask[];
  suggestedTag: string | null;
  reasoning: string | null;
}

export interface CreateActionWithSubtasksRequest {
  title: string;
  detail?: string | null;
  estimateMinutes?: number | null;
  subtasks: BreakdownSubtask[];
  workspace?: string | null;
  domain?: string | null;
  priority?: string | null;
}

@Injectable({ providedIn: 'root' })
export class BreakdownService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/+$/, '');

  async propose(text: string, depth: BreakdownDepth = 'Standard'): Promise<BreakdownProposal> {
    return await firstValueFrom(
      this.http.post<BreakdownProposal>(
        `${this.base}/api/breakdown`,
        { text, depth },
      ),
    );
  }

  async materialize(req: CreateActionWithSubtasksRequest): Promise<ActionItemSummary> {
    return await firstValueFrom(
      this.http.post<ActionItemSummary>(
        `${this.base}/api/actions/with-subtasks`,
        req,
      ),
    );
  }
}
